import {
  getClaimCandidate as apiGetClaimCandidate,
  listClaimCandidates as apiListClaimCandidates,
  updateClaimCandidateStatus as apiUpdateClaimCandidateStatus,
  updateDraftArgument as apiUpdateDraftArgument,
  revertDraftArgument as apiRevertDraftArgument,
  validateClaimTranches as apiValidateClaimTranches,
} from "@hp/api-client";
import type { ClaimCandidate } from "@hp/types";
import type {
  ClaimCandidateDetail,
  ListClaimCandidatesParams,
  ListClaimCandidatesResponse,
  UpdateClaimCandidateStatusRequest,
} from "./types";

/**
 * List claim candidates, optionally filtered by status and/or issue tag.
 * Plain async function - wrap in `useQuery` with a queryKey of
 * `["claim-candidates", params]`.
 */
export async function listClaimCandidates(
  params: ListClaimCandidatesParams = {},
  signal?: AbortSignal,
): Promise<ListClaimCandidatesResponse> {
  return apiListClaimCandidates(params, signal);
}

/**
 * Get one claim candidate with its full precedent chain resolved to
 * Findings + their current AuthorityStatus. Wrap in `useQuery` with a
 * queryKey of `["claim-candidates", id]`.
 */
export async function getClaimCandidate(id: string, signal?: AbortSignal): Promise<ClaimCandidateDetail> {
  return apiGetClaimCandidate(id, signal);
}

/**
 * Accept (validate), reject, or defer a claim candidate. `rejection_reason`
 * carries the reviewer's required explanation for both "rejected" and
 * "deferred" transitions. Wrap in `useMutation`, then invalidate
 * `["claim-candidates"]` (list) and `["claim-candidates", id]` (detail) on
 * success.
 */
export async function updateClaimCandidateStatus(
  id: string,
  request: UpdateClaimCandidateStatusRequest,
): Promise<ClaimCandidate> {
  return apiUpdateClaimCandidateStatus(id, request);
}

/** Saves a reviewer's edits to the draft argument. `draft_argument_original` is never touched by this. */
export async function updateDraftArgument(id: string, draftArgument: string): Promise<ClaimCandidate> {
  return apiUpdateDraftArgument(id, draftArgument);
}

/** Discards a reviewer's edits, restoring `draft_argument` to `draft_argument_original`. */
export async function revertDraftArgument(id: string): Promise<ClaimCandidate> {
  return apiRevertDraftArgument(id);
}

export async function validateClaimTranches(claimIds: string[]): Promise<ClaimCandidate[]> {
  return apiValidateClaimTranches(claimIds);
}
